import ReactMarkdown from "react-markdown";
import { StepCard } from "./StepCard";
import { ProTipCard } from "./ProTipCard";

interface InteractiveMarkdownProps {
  content: string;
  articleSlug: string;
}

// Recursively extract text content from ReactMarkdown children
function extractTextContent(children: any): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(extractTextContent).join("");
  if (children?.props?.children) return extractTextContent(children.props.children);
  return "";
}

export function InteractiveMarkdown({ content, articleSlug }: InteractiveMarkdownProps) {
  return (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <ReactMarkdown
        components={{
        h1: ({ node, ...props }) => {
          const text = String(props.children).replace(/\s*\{#[^}]+\}\s*$/g, "").trim();
          const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
          return <h1 id={id} className="scroll-mt-20">{text}</h1>;
        },
        h2: ({ node, ...props }) => {
          const text = String(props.children).replace(/\s*\{#[^}]+\}\s*$/g, "").trim();
          const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
          return <h2 id={id} className="scroll-mt-20">{text}</h2>;
        },
        h3: ({ node, ...props }) => {
          const text = String(props.children).replace(/\s*\{#[^}]+\}\s*$/g, "").trim();
          const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
          return <h3 id={id} className="scroll-mt-20">{text}</h3>;
        },
        ol: ({ node, ...props }) => {
          const children = props.children as any[];
          const items = children.filter((child) => child?.type === "li");
          
          // Only convert to step cards if:
          // 1. Has 3-10 items (substantial but not too long)
          // 2. Each item has meaningful content (more than just a few words)
          if (items.length >= 3 && items.length <= 10) {
            const hasSubstantialContent = items.every((item) => {
              const text = extractTextContent(item.props?.children);
              return text.length > 20; // At least 20 characters per step
            });
            
            if (hasSubstantialContent) {
              return (
                <div className="space-y-4 my-8 not-prose">
                  {items.map((item, index) => {
                    const textContent = extractTextContent(item.props?.children);
                    
                    return (
                      <StepCard
                        key={index}
                        stepNumber={index + 1}
                        content={textContent}
                        articleSlug={articleSlug}
                      />
                    );
                  })}
                </div>
              );
            }
          }
          
          return <ol {...props} />;
        },
        blockquote: ({ node, ...props }) => {
          const text = extractTextContent(props.children);
          
          // Check if it's a Pro Tip (starts with "Pro Tip" or contains 💡)
          if (text.toLowerCase().includes("pro tip") || text.includes("💡")) {
            return <ProTipCard>{props.children}</ProTipCard>;
          }
          
          return <blockquote {...props} />;
        },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
